/**
 * Main Application Component
 *
 * AI Cloud - BOM Calculator - Modular Architecture
 */

import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useStore } from './store/store';
import { useCalculation } from './hooks/useCalculation';
import { useAuth } from './hooks/useAuth';
import { Section } from './components/layout/Section';
import { Table } from './components/display/Table';
import { MetricCard } from './components/display/MetricCard';
import { NumericInput } from './components/forms/NumericInput';
import { TextInput } from './components/forms/TextInput';
import { ImportExportPanel } from './components/io/ImportExportPanel';
import { DensityControlPanel } from './components/power/DensityControlPanel';
import { ProfileSelectionPanel } from './components/configuration/ProfileSelectionPanel';
import { RoutingConfigPanel } from './components/configuration/RoutingConfigPanel';
import { OversubscriptionCalculator } from './components/network/OversubscriptionCalculator';
import { CableSpecificationPanel } from './components/network/CableSpecificationPanel';
import { TopologyVisualization } from './components/visualization/TopologyVisualization';
import { ProfileManagement } from './components/admin/ProfileManagement';
import { AccessControlManagement } from './components/admin/access-control';
import { AuthButton } from './components/auth/AuthButton';
import { LandingPage } from './components/auth/LandingPage';
import { ALL_SWITCHES } from './data/switches';
import { ALL_SERVERS, getServersByVendor } from './data/servers';
import { getCPUComputeNodes, getInfrastructureNodes, getNodeById } from './data/nodes';

function MainApp() {
  console.log('[App] Rendering App component');

  const configuration = useStore((state) => state.configuration);
  console.log('[App] Configuration loaded:', configuration);

  // Filter switches
  const ibSwitches = ALL_SWITCHES.filter((s) => s.fabricType === 'InfiniBand');
  const ethernetSwitches = ALL_SWITCHES.filter(
    (s) => s.fabricType === 'Ethernet' || s.fabricType === 'RoCE'
  );
  const managementSwitches = ALL_SWITCHES.filter(
    (s) => s.fabricType === 'Ethernet' && s.ports.totalPorts <= 64
  );

  // Get unique server vendors for organizing dropdowns
  const serverVendors = Array.from(new Set(ALL_SERVERS.map((s) => s.vendor))).sort();

  // Get node profiles for additional nodes
  const cpuComputeProfiles = getCPUComputeNodes();
  const infrastructureProfiles = getInfrastructureNodes();

  const {
    setTotalGPUs,
    setGPUsPerNode,
    updateServer,
    updateNetwork,
    updateRack,
    updatePower,
    updateAdditionalNodes,
  } = useStore();

  const { calculate, isCalculating } = useCalculation();

  const expandedSections = useStore((state) => state.expandedSections);
  const toggleSection = useStore((state) => state.toggleSection);

  // Tab state
  const [activeTab, setActiveTab] = useState<'bom' | 'topology' | 'profiles' | 'admin'>('bom');
  const { isAdmin } = useAuth();

  // Initial calculation
  useEffect(() => {
    console.log('[App] Running initial calculation');
    try {
      calculate();
    } catch (error) {
      console.error('[App] Calculation error:', error);
    }
  }, []);

  // Apply server profile to configuration
  const applyServerProfile = (serverId: string) => {
    const server = ALL_SERVERS.find((s) => s.id === serverId);
    if (!server) return;

    // Update server specs
    updateServer({
      model: server.model,
      rackUnits: server.dimensions.rackUnits,
    });

    // Update power
    updatePower({
      powerPerNodeKw: server.power.typicalPowerWatts / 1000,
    });

    // Update rack density
    updateRack({
      serversPerRack: Math.floor(48 / server.dimensions.rackUnits),
    });

    // Update GPU count if present
    if (server.gpu) {
      setGPUsPerNode(server.gpu.gpusPerNode);
    }
  };

  // Apply IB switch to configuration
  const applyIBSwitch = (switchId: string) => {
    const sw = ALL_SWITCHES.find((s) => s.id === switchId);
    if (!sw) return;

    updateNetwork({
      eastWest: {
        ...configuration.network.eastWest,
        fabricType: sw.fabricType as any,
        leafSwitch: {
          ...configuration.network.eastWest.leafSwitch,
          model: sw.productName,
          manufacturer: sw.vendor,
          ports: sw.ports.totalPorts,
          powerWatts: sw.power?.typicalPowerWatts || 850,
        },
        spineSwitch: {
          ...configuration.network.eastWest.spineSwitch,
          model: sw.productName,
          manufacturer: sw.vendor,
          ports: sw.ports.totalPorts,
          powerWatts: sw.power?.typicalPowerWatts || 850,
        },
      },
    });
  };

  // Apply NS Leaf switch to configuration
  const applyNSLeafSwitch = (switchId: string) => {
    const sw = ALL_SWITCHES.find((s) => s.id === switchId);
    if (!sw) return;

    updateNetwork({
      northSouth: {
        ...configuration.network.northSouth,
        fabricType: sw.fabricType as any,
        leafSwitch: {
          ...configuration.network.northSouth.leafSwitch,
          model: sw.productName,
          manufacturer: sw.vendor,
          ports: sw.ports.totalPorts,
          powerWatts: sw.power?.typicalPowerWatts || 650,
        },
      },
    });
  };

  // Apply NS Spine switch to configuration
  const applyNSSpineSwitch = (switchId: string) => {
    const sw = ALL_SWITCHES.find((s) => s.id === switchId);
    if (!sw) return;

    updateNetwork({
      northSouth: {
        ...configuration.network.northSouth,
        spineSwitch: {
          ...configuration.network.northSouth.spineSwitch,
          model: sw.productName,
          manufacturer: sw.vendor,
          ports: sw.ports.totalPorts,
          powerWatts: sw.power?.typicalPowerWatts || 650,
        },
      },
    });
  };

  // Apply OOB switch to configuration
  const applyOOBSwitch = (switchId: string) => {
    const sw = ALL_SWITCHES.find((s) => s.id === switchId);
    if (!sw) return;

    updateNetwork({
      management: {
        ...configuration.network.management,
        switchModel: sw.productName,
        portsPerSwitch: sw.ports.totalPorts,
      },
    });
  };

  const { scaling, gpu, server, network, rack, power, additionalNodes } = configuration;
  const nodes = Math.ceil(scaling.totalGPUs / scaling.gpusPerNode);

  // Calculate total nodes including CPU and Infrastructure nodes
  const cpuNodeCount = additionalNodes.cpuNodes.enabled ? additionalNodes.cpuNodes.count : 0;
  const infraNodeCount = additionalNodes.infraNodes.enabled ? additionalNodes.infraNodes.count : 0;
  const totalNodes = nodes + cpuNodeCount + infraNodeCount;

  // Network calculations (simplified from BOM calculator)
  // East/West InfiniBand - using blocking factor
  const ibBlockingFactor = network.eastWest.blockingFactor || 1.0;
  const ibTotalPorts = network.eastWest.leafSwitch.ports;
  const ibDownlinks = Math.floor(ibTotalPorts * ibBlockingFactor / (ibBlockingFactor + 1));
  const ibUplinks = ibTotalPorts - ibDownlinks;

  const ibTotalLinks = nodes * network.eastWest.portsPerNode;
  const ibMinLeafsForCapacity = Math.ceil(ibTotalLinks / ibDownlinks);
  const ibMinLeafsForDiversity = network.eastWest.portsPerNode;
  const ibLeaf = Math.max(ibMinLeafsForCapacity, ibMinLeafsForDiversity);

  // Calculate spine switches using bundle size approach (ClusterDesign.org)
  const ibSpinePorts = network.eastWest.spineSwitch.ports;
  const ibBundleSize = Math.floor(ibSpinePorts / ibLeaf);
  const ibSpine = ibBundleSize >= 1 ? Math.ceil(ibUplinks / ibBundleSize) : Math.ceil(ibLeaf);

  const ibCablesNodeLeaf = ibTotalLinks; // 1:1 mapping to server IB ports
  const ibCablesLeafSpine = ibLeaf * ibUplinks;

  // Get node profiles for Ethernet port counts
  const cpuNodeProfile = additionalNodes.cpuNodes.enabled
    ? getNodeById(additionalNodes.cpuNodes.profileId)
    : null;
  const infraNodeProfile = additionalNodes.infraNodes.enabled
    ? getNodeById(additionalNodes.infraNodes.profileId)
    : null;

  // North/South Ethernet - using blocking factor (includes ALL nodes: GPU + CPU + Infrastructure)
  const nsBlockingFactor = network.northSouth.blockingFactor || 1.0;
  const nsTotalPorts = network.northSouth.leafSwitch.ports;
  const nsDownlinks = Math.floor(nsTotalPorts * nsBlockingFactor / (nsBlockingFactor + 1));
  const nsUplinks = nsTotalPorts - nsDownlinks;

  // Calculate total Ethernet ports needed from all nodes (per-node Ethernet ports)
  // Default to 2 ports if not specified (for backward compatibility)
  const gpuNodeEthernetPorts = nodes * (server.ethernetPorts || 2);
  const cpuNodeEthernetPorts = cpuNodeProfile && additionalNodes.cpuNodes.enabled
    ? additionalNodes.cpuNodes.count * (cpuNodeProfile.ethernetPorts || 2)
    : 0;
  const infraNodeEthernetPorts = infraNodeProfile && additionalNodes.infraNodes.enabled
    ? additionalNodes.infraNodes.count * (infraNodeProfile.ethernetPorts || 2)
    : 0;
  const nsTotalLinks = gpuNodeEthernetPorts + cpuNodeEthernetPorts + infraNodeEthernetPorts;

  const nsMinLeafsForCapacity = Math.ceil(nsTotalLinks / nsDownlinks);
  // Use max Ethernet ports per node for diversity requirement
  const maxEthernetPortsPerNode = Math.max(
    server.ethernetPorts || 2,
    cpuNodeProfile?.ethernetPorts || 0,
    infraNodeProfile?.ethernetPorts || 0
  );
  const nsMinLeafsForDiversity = maxEthernetPortsPerNode;
  const nsLeaf = Math.max(nsMinLeafsForCapacity, nsMinLeafsForDiversity);

  // Apply High Availability multiplier for spine switches
  const nsHaMultiplier = network.northSouth.highAvailabilitySpine ? 2 : 1;
  const nsSpine = Math.ceil((nsLeaf * nsUplinks) / network.northSouth.spineSwitch.ports) * nsHaMultiplier;
  const nsCablesNodeLeaf = nsTotalLinks; // Total Ethernet cables = sum of all node Ethernet ports
  const nsCablesLeafSpine = (nsLeaf * nsUplinks) * nsHaMultiplier;

  // Management OOB - includes all nodes: GPU + CPU + Infrastructure
  const oobHost = totalNodes;
  const oobDpu = totalNodes * network.management.dpusPerNode * network.management.oobPortsPerDPU;
  const oobSwitches = ibLeaf + ibSpine + nsLeaf + nsSpine;
  const oobTotal = oobHost + oobDpu + oobSwitches;
  const oobSwitchCount = Math.ceil(oobTotal / network.management.portsPerSwitch);

  const computeRacks = Math.ceil(nodes / rack.serversPerRack);
  const computeRuUsed = rack.serversPerRack * server.rackUnits;
  const rackPowerKw = rack.serversPerRack * power.powerPerNodeKw;

  const ibSwitchRu = (ibLeaf + ibSpine) * 1;
  const nsSwitchRu = (nsLeaf + nsSpine) * 2;
  const oobSwitchRu = oobSwitchCount * 1;
  const cableMgmtRu = Math.ceil((ibLeaf + ibSpine + nsLeaf + nsSpine + oobSwitchCount) * 0.5);
  const totalSwitchRu = ibSwitchRu + nsSwitchRu + oobSwitchRu + cableMgmtRu;
  const switchRacks = Math.ceil(totalSwitchRu / 48);

  const totalRacks = computeRacks + switchRacks;
  const totalComputePowerKw = computeRacks * rackPowerKw;

  return (
    <div className="font-sans max-w-7xl mx-auto p-4 bg-white">
      {/* Header */}
      <div className="bg-indigo-950 text-indigo-100 p-4 rounded-lg mb-5">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-lg font-bold">AI Cloud - BOM Calculator</div>
            {isCalculating && (
              <div className="text-xs mt-2 text-yellow-300">Calculating...</div>
            )}
          </div>

          {/* Auth Button */}
          <div className="flex-shrink-0">
            <AuthButton />
          </div>

          {/* Tab Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('bom')}
              className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                activeTab === 'bom'
                  ? 'bg-indigo-100 text-indigo-950'
                  : 'bg-indigo-800 text-indigo-100 hover:bg-indigo-700'
              }`}
            >
              BOM & Tables
            </button>
            <button
              onClick={() => setActiveTab('topology')}
              className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                activeTab === 'topology'
                  ? 'bg-indigo-100 text-indigo-950'
                  : 'bg-indigo-800 text-indigo-100 hover:bg-indigo-700'
              }`}
            >
              Topology Diagram
            </button>
            <button
              onClick={() => setActiveTab('profiles')}
              className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                activeTab === 'profiles'
                  ? 'bg-indigo-100 text-indigo-950'
                  : 'bg-indigo-800 text-indigo-100 hover:bg-indigo-700'
              }`}
            >
              Profiles
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab('admin')}
                className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                  activeTab === 'admin'
                    ? 'bg-indigo-100 text-indigo-950'
                    : 'bg-indigo-800 text-indigo-100 hover:bg-indigo-700'
                }`}
              >
                Admin
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-4 flex-wrap">
        {activeTab !== 'admin' && (
          <div className="flex-none w-72 bg-purple-50 border border-gray-200 rounded-lg p-4">
            <div className="font-bold text-sm mb-3">Configuration</div>

          <ProfileSelectionPanel />

          <RoutingConfigPanel />

          <OversubscriptionCalculator />

          <CableSpecificationPanel />

          <Section
            title="Compute"
            collapsible
            isExpanded={expandedSections.has('compute')}
            onToggle={() => toggleSection('compute')}
          >
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Server Profile
              </label>
              <select
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                onChange={(e) => applyServerProfile(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>
                  Select server profile...
                </option>
                {serverVendors.map((vendor) => (
                  <optgroup key={vendor} label={vendor}>
                    {getServersByVendor(vendor).map((server) => (
                      <option key={server.id} value={server.id}>
                        {server.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <NumericInput
              label="Total GPUs"
              value={scaling.totalGPUs}
              onChange={setTotalGPUs}
              step={8}
              note={`→ ${nodes} nodes`}
            />

            <TextInput
              label="GPU Type"
              value={gpu.platformName}
              onChange={(v) => updateServer({ model: v })}
            />

            <NumericInput
              label="GPUs per Node"
              value={scaling.gpusPerNode}
              onChange={setGPUsPerNode}
              note={`→ ${nodes} nodes`}
            />

            <NumericInput
              label="Nodes per Rack"
              value={rack.serversPerRack}
              onChange={(v) => updateRack({ serversPerRack: v })}
            />

            <NumericInput
              label="RU per Node"
              value={server.rackUnits}
              onChange={(v) => updateServer({ rackUnits: v })}
            />

            <NumericInput
              label="Power per Node"
              value={power.powerPerNodeKw}
              onChange={(v) => updatePower({ powerPerNodeKw: v })}
              step={0.1}
              unit="kW"
            />
          </Section>

          <Section
            title="Additional Nodes (CPU & Infra)"
            collapsible
            isExpanded={expandedSections.has('additional-nodes')}
            onToggle={() => toggleSection('additional-nodes')}
          >
            {/* CPU Nodes */}
            <div className="mb-4">
              <label className="flex items-center space-x-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={configuration.additionalNodes.cpuNodes.enabled}
                  onChange={(e) =>
                    updateAdditionalNodes({
                      cpuNodes: {
                        ...configuration.additionalNodes.cpuNodes,
                        enabled: e.target.checked,
                      },
                    })
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-xs font-semibold text-gray-700">
                  CPU-only Compute Nodes
                </span>
              </label>

              {configuration.additionalNodes.cpuNodes.enabled && (
                <div className="ml-6 space-y-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Profile
                    </label>
                    <select
                      value={configuration.additionalNodes.cpuNodes.profileId}
                      onChange={(e) =>
                        updateAdditionalNodes({
                          cpuNodes: {
                            ...configuration.additionalNodes.cpuNodes,
                            profileId: e.target.value,
                          },
                        })
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                    >
                      {cpuComputeProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name} ({profile.rackUnits}U, {profile.powerKw}kW)
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const profile = getNodeById(
                        configuration.additionalNodes.cpuNodes.profileId
                      );
                      return profile ? (
                        <p className="text-[10px] text-gray-500 mt-1">
                          {profile.description}
                        </p>
                      ) : null;
                    })()}
                  </div>
                  <NumericInput
                    label="Node Count"
                    value={configuration.additionalNodes.cpuNodes.count}
                    onChange={(v) =>
                      updateAdditionalNodes({
                        cpuNodes: {
                          ...configuration.additionalNodes.cpuNodes,
                          count: v,
                        },
                      })
                    }
                    min={0}
                  />
                </div>
              )}
            </div>

            {/* Infrastructure Nodes */}
            <div className="mb-4">
              <label className="flex items-center space-x-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={configuration.additionalNodes.infraNodes.enabled}
                  onChange={(e) =>
                    updateAdditionalNodes({
                      infraNodes: {
                        ...configuration.additionalNodes.infraNodes,
                        enabled: e.target.checked,
                      },
                    })
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-xs font-semibold text-gray-700">
                  Infrastructure Nodes (Login/Storage/Management)
                </span>
              </label>

              {configuration.additionalNodes.infraNodes.enabled && (
                <div className="ml-6 space-y-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Profile
                    </label>
                    <select
                      value={configuration.additionalNodes.infraNodes.profileId}
                      onChange={(e) =>
                        updateAdditionalNodes({
                          infraNodes: {
                            ...configuration.additionalNodes.infraNodes,
                            profileId: e.target.value,
                          },
                        })
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                    >
                      {infrastructureProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name} ({profile.rackUnits}U, {profile.powerKw}kW)
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const profile = getNodeById(
                        configuration.additionalNodes.infraNodes.profileId
                      );
                      return profile ? (
                        <p className="text-[10px] text-gray-500 mt-1">
                          {profile.description}
                        </p>
                      ) : null;
                    })()}
                  </div>
                  <NumericInput
                    label="Node Count"
                    value={configuration.additionalNodes.infraNodes.count}
                    onChange={(v) =>
                      updateAdditionalNodes({
                        infraNodes: {
                          ...configuration.additionalNodes.infraNodes,
                          count: v,
                        },
                      })
                    }
                    min={0}
                  />
                </div>
              )}
            </div>
          </Section>

          <Section
            title="East/West - InfiniBand"
            collapsible
            isExpanded={expandedSections.has('network-ew')}
            onToggle={() => toggleSection('network-ew')}
          >
            <TextInput
              label="Fabric Type"
              value={network.eastWest.fabricType}
              onChange={(v) =>
                updateNetwork({
                  eastWest: { ...network.eastWest, fabricType: v as any },
                })
              }
            />

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                InfiniBand Switch Profile
              </label>
              <select
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                onChange={(e) => applyIBSwitch(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>
                  Select switch profile...
                </option>
                {ibSwitches.length > 0 && ibSwitches.map((sw) => (
                  <option key={sw.id} value={sw.id}>
                    {sw.vendor} - {sw.productName}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Topology Type
              </label>
              <select
                value={network.eastWest.topologyType || 'fat-tree'}
                onChange={(e) =>
                  updateNetwork({
                    eastWest: { ...network.eastWest, topologyType: e.target.value },
                  })
                }
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
              >
                <option value="fat-tree">Fat-Tree (Traditional Leaf-Spine)</option>
                <option value="rail-optimized">Rail-Optimized Fat-Tree</option>
                <option value="dragonfly">Dragonfly+ (Group-Based)</option>
              </select>
              {network.eastWest.topologyType === 'fat-tree' && (
                <p className="text-xs text-gray-600 mt-1">Traditional 2-tier leaf-spine design, best for mixed workloads</p>
              )}
              {network.eastWest.topologyType === 'rail-optimized' && (
                <p className="text-xs text-gray-600 mt-1">Optimized for multi-GPU nodes with NVLink, reduces switch count</p>
              )}
              {network.eastWest.topologyType === 'dragonfly' && (
                <p className="text-xs text-gray-600 mt-1">Cost-effective for large clusters (500+ nodes), optimized for locality</p>
              )}
            </div>

            {network.eastWest.topologyType === 'rail-optimized' && (
              <NumericInput
                label="Number of Rails"
                value={network.eastWest.railsCount || 4}
                onChange={(v) =>
                  updateNetwork({
                    eastWest: { ...network.eastWest, railsCount: v },
                  })
                }
                min={2}
                max={16}
                note="Each rail has dedicated spine switches"
              />
            )}

            {network.eastWest.topologyType === 'dragonfly' && (
              <>
                <NumericInput
                  label="Number of Groups"
                  value={network.eastWest.dragonflyGroups || 8}
                  onChange={(v) =>
                    updateNetwork({
                      eastWest: { ...network.eastWest, dragonflyGroups: v },
                    })
                  }
                  min={2}
                  max={64}
                  note="Logical groups in dragonfly topology"
                />
                <NumericInput
                  label="Routers per Group"
                  value={network.eastWest.dragonflyRoutersPerGroup || 16}
                  onChange={(v) =>
                    updateNetwork({
                      eastWest: { ...network.eastWest, dragonflyRoutersPerGroup: v },
                    })
                  }
                  min={2}
                  max={32}
                  note="Switches/routers in each group"
                />
              </>
            )}

            <TextInput
              label="Switch Model"
              value={network.eastWest.leafSwitch.model}
              onChange={(v) =>
                updateNetwork({
                  eastWest: {
                    ...network.eastWest,
                    leafSwitch: { ...network.eastWest.leafSwitch, model: v },
                    spineSwitch: { ...network.eastWest.spineSwitch, model: v },
                  },
                })
              }
            />

            <NumericInput
              label="Ports per IB Switch"
              value={network.eastWest.leafSwitch.ports}
              onChange={(v) =>
                updateNetwork({
                  eastWest: {
                    ...network.eastWest,
                    leafSwitch: { ...network.eastWest.leafSwitch, ports: v },
                    spineSwitch: { ...network.eastWest.spineSwitch, ports: v },
                  },
                })
              }
            />

            <NumericInput
              label="Blocking Factor"
              value={network.eastWest.blockingFactor || 1.0}
              onChange={(v) =>
                updateNetwork({
                  eastWest: { ...network.eastWest, blockingFactor: v },
                })
              }
              step={0.5}
              min={1.0}
              max={4.0}
              note={`${ibDownlinks} down / ${ibUplinks} up (1.0=non-blocking)`}
            />

            <NumericInput
              label="IB Ports per Node"
              value={network.eastWest.portsPerNode}
              onChange={(v) =>
                updateNetwork({
                  eastWest: { ...network.eastWest, portsPerNode: v },
                })
              }
            />
          </Section>

          <Section
            title="North/South - Ethernet"
            collapsible
            isExpanded={expandedSections.has('network-ns')}
            onToggle={() => toggleSection('network-ns')}
          >
            <TextInput
              label="Fabric Type"
              value={network.northSouth.fabricType}
              onChange={(v) =>
                updateNetwork({
                  northSouth: { ...network.northSouth, fabricType: v as any },
                })
              }
            />

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Ethernet Leaf Switch Profile
              </label>
              <select
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                onChange={(e) => applyNSLeafSwitch(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>
                  Select leaf switch profile...
                </option>
                {ethernetSwitches.length > 0 && ethernetSwitches.map((sw) => (
                  <option key={sw.id} value={sw.id}>
                    {sw.vendor} - {sw.productName}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Ethernet Spine Switch Profile
              </label>
              <select
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                onChange={(e) => applyNSSpineSwitch(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>
                  Select spine switch profile...
                </option>
                {ethernetSwitches.length > 0 && ethernetSwitches.map((sw) => (
                  <option key={sw.id} value={sw.id}>
                    {sw.vendor} - {sw.productName}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Topology Type
              </label>
              <select
                value={network.northSouth.topologyType || 'fat-tree'}
                onChange={(e) =>
                  updateNetwork({
                    northSouth: { ...network.northSouth, topologyType: e.target.value },
                  })
                }
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
              >
                <option value="fat-tree">Fat-Tree (Traditional Leaf-Spine)</option>
                <option value="rail-optimized">Rail-Optimized Fat-Tree</option>
                <option value="dragonfly">Dragonfly+ (Group-Based)</option>
              </select>
              {network.northSouth.topologyType === 'fat-tree' && (
                <p className="text-xs text-gray-600 mt-1">Traditional 2-tier leaf-spine design, best for mixed workloads</p>
              )}
              {network.northSouth.topologyType === 'rail-optimized' && (
                <p className="text-xs text-gray-600 mt-1">Optimized for multi-GPU nodes with NVLink, reduces switch count</p>
              )}
              {network.northSouth.topologyType === 'dragonfly' && (
                <p className="text-xs text-gray-600 mt-1">Cost-effective for large clusters (500+ nodes), optimized for locality</p>
              )}
            </div>

            {network.northSouth.topologyType === 'rail-optimized' && (
              <NumericInput
                label="Number of Rails"
                value={network.northSouth.railsCount || 4}
                onChange={(v) =>
                  updateNetwork({
                    northSouth: { ...network.northSouth, railsCount: v },
                  })
                }
                min={2}
                max={16}
                note="Each rail has dedicated spine switches"
              />
            )}

            {network.northSouth.topologyType === 'dragonfly' && (
              <>
                <NumericInput
                  label="Number of Groups"
                  value={network.northSouth.dragonflyGroups || 8}
                  onChange={(v) =>
                    updateNetwork({
                      northSouth: { ...network.northSouth, dragonflyGroups: v },
                    })
                  }
                  min={2}
                  max={64}
                  note="Logical groups in dragonfly topology"
                />
                <NumericInput
                  label="Routers per Group"
                  value={network.northSouth.dragonflyRoutersPerGroup || 16}
                  onChange={(v) =>
                    updateNetwork({
                      northSouth: { ...network.northSouth, dragonflyRoutersPerGroup: v },
                    })
                  }
                  min={2}
                  max={32}
                  note="Switches/routers in each group"
                />
              </>
            )}

            <TextInput
              label="Switch Model"
              value={network.northSouth.leafSwitch.model}
              onChange={(v) =>
                updateNetwork({
                  northSouth: {
                    ...network.northSouth,
                    leafSwitch: { ...network.northSouth.leafSwitch, model: v },
                    spineSwitch: { ...network.northSouth.spineSwitch, model: v },
                  },
                })
              }
            />

            <NumericInput
              label="Ports per NS Switch"
              value={network.northSouth.leafSwitch.ports}
              onChange={(v) =>
                updateNetwork({
                  northSouth: {
                    ...network.northSouth,
                    leafSwitch: { ...network.northSouth.leafSwitch, ports: v },
                    spineSwitch: { ...network.northSouth.spineSwitch, ports: v },
                  },
                })
              }
            />

            <NumericInput
              label="Blocking Factor"
              value={network.northSouth.blockingFactor || 1.0}
              onChange={(v) =>
                updateNetwork({
                  northSouth: { ...network.northSouth, blockingFactor: v },
                })
              }
              step={0.5}
              min={1.0}
              max={4.0}
              note={`${nsDownlinks} down / ${nsUplinks} up (1.0=non-blocking)`}
            />

            <div className="mb-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={network.northSouth.highAvailabilitySpine || false}
                  onChange={(e) =>
                    updateNetwork({
                      northSouth: {
                        ...network.northSouth,
                        highAvailabilitySpine: e.target.checked,
                      },
                    })
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-xs font-semibold text-gray-700">
                  High Availability Spine (2× spine switches)
                </span>
              </label>
              <p className="text-[10px] text-gray-500 mt-1 ml-6">
                Doubles spine switches for redundancy
              </p>
            </div>

            <div className="text-[10px] text-gray-600 mt-3 p-2 bg-blue-50 rounded border border-blue-200">
              <strong>Note:</strong> Ethernet ports per node are configured in each node profile. GPU servers use server.ethernetPorts, CPU and Infrastructure nodes use their profile's ethernetPorts field.
            </div>
          </Section>

          <Section
            title="OOB / Management"
            collapsible
            isExpanded={expandedSections.has('network-mgmt')}
            onToggle={() => toggleSection('network-mgmt')}
          >
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Management Switch Profile
              </label>
              <select
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                onChange={(e) => applyOOBSwitch(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>
                  Select switch profile...
                </option>
                {managementSwitches.length > 0 && managementSwitches.map((sw) => (
                  <option key={sw.id} value={sw.id}>
                    {sw.vendor} - {sw.productName}
                  </option>
                ))}
              </select>
            </div>

            <TextInput
              label="OOB Switch Model"
              value={network.management.switchModel}
              onChange={(v) =>
                updateNetwork({
                  management: { ...network.management, switchModel: v },
                })
              }
            />

            <NumericInput
              label="OOB Ports per Switch"
              value={network.management.portsPerSwitch}
              onChange={(v) =>
                updateNetwork({
                  management: { ...network.management, portsPerSwitch: v },
                })
              }
            />

            <NumericInput
              label="DPUs per Node"
              value={network.management.dpusPerNode}
              onChange={(v) =>
                updateNetwork({
                  management: { ...network.management, dpusPerNode: v },
                })
              }
            />

            <NumericInput
              label="OOB Ports per DPU"
              value={network.management.oobPortsPerDPU}
              onChange={(v) =>
                updateNetwork({
                  management: { ...network.management, oobPortsPerDPU: v },
                })
              }
            />
          </Section>
          </div>
        )}

        {activeTab !== 'profiles' && activeTab !== 'admin' && (
          <div className="flex-none w-80 space-y-4">
            <ImportExportPanel />
            <DensityControlPanel />
          </div>
        )}

        {/* Results Panel */}
        <div className="flex-1 min-w-0">
          {activeTab === 'profiles' ? (
            <ProfileManagement />
          ) : activeTab === 'admin' && isAdmin ? (
            <AccessControlManagement />
          ) : activeTab === 'bom' ? (
            <>
              {/* Cluster Summary Metrics */}
              <div className="grid grid-cols-4 gap-3 mb-5">
                <MetricCard label="Total GPUs" value={scaling.totalGPUs} />
                <MetricCard label="Compute Nodes" value={nodes} />
                <MetricCard label="Total Racks" value={totalRacks} />
                <MetricCard
                  label="Total Power"
                  value={totalComputePowerKw.toFixed(1)}
                  unit="kW"
                />
              </div>

              <Section title="1 — Cluster Summary">
            <Table
              headers={['Component', 'Per Node', 'Cluster Total']}
              rows={[
                [gpu.platformName, scaling.gpusPerNode, scaling.totalGPUs],
                ['Compute Nodes', 1, nodes],
                [
                  `${network.eastWest.fabricType} Links (E/W)`,
                  network.eastWest.portsPerNode,
                  ibTotalLinks,
                ],
                [
                  `${network.northSouth.fabricType} Links (N/S)`,
                  'Per node profile',
                  nsTotalLinks,
                ],
                ['DPUs', network.management.dpusPerNode, nodes * network.management.dpusPerNode],
                ['Host BMC / OOB Ports', 1, nodes],
              ]}
            />
          </Section>

          <Section title="2 — East/West Fabric (InfiniBand)">
            <Table
              headers={['Layer', 'Switch Model', 'Count', 'Downlinks Used', 'Uplinks Used', 'Note']}
              rows={[
                [
                  'Leaf',
                  network.eastWest.leafSwitch.model,
                  ibLeaf,
                  `${ibDownlinks}/sw (${ibLeaf * ibDownlinks} total)`,
                  `${ibUplinks}/sw (${ibLeaf * ibUplinks} total)`,
                  `ceil(${ibTotalLinks} node ports ÷ ${ibDownlinks})`,
                ],
                [
                  'Spine',
                  network.eastWest.spineSwitch.model,
                  ibSpine,
                  '—',
                  `${ibLeaf * ibUplinks} leaf uplinks absorbed`,
                  `ceil(${ibLeaf * ibUplinks} ÷ ${network.eastWest.spineSwitch.ports})`,
                ],
              ]}
              footer={['TOTAL', '', ibLeaf + ibSpine, '', '', '']}
            />
            <Table
              headers={['Cable / Optic', 'Count', 'Derivation']}
              rows={[
                [
                  `Node → Leaf (${network.eastWest.fabricType})`,
                  ibCablesNodeLeaf,
                  `${ibLeaf} leaf × ${ibDownlinks} downlinks (full SU; ${nodes * network.eastWest.portsPerNode} used)`,
                ],
                [
                  `Leaf → Spine (${network.eastWest.fabricType})`,
                  ibCablesLeafSpine,
                  `${ibLeaf} leaf × ${ibUplinks} uplinks`,
                ],
              ]}
              footer={['TOTAL', ibCablesNodeLeaf + ibCablesLeafSpine, '']}
            />
          </Section>

          <Section title="3 — North/South Fabric (Ethernet)">
            <Table
              headers={['Layer', 'Switch Model', 'Count', 'Downlinks Used', 'Uplinks Used', 'Note']}
              rows={[
                [
                  'Leaf (ToR/EoR)',
                  network.northSouth.leafSwitch.model,
                  nsLeaf,
                  `${nsDownlinks}/sw (${nsLeaf * nsDownlinks} cap, ${nsCablesNodeLeaf} used)`,
                  `${nsUplinks}/sw`,
                  `ceil(${nsTotalLinks} ÷ ${nsDownlinks})`,
                ],
                [
                  'Spine',
                  network.northSouth.spineSwitch.model,
                  nsSpine,
                  '—',
                  `${nsLeaf * nsUplinks} leaf uplinks`,
                  `ceil(${nsLeaf * nsUplinks} ÷ ${network.northSouth.spineSwitch.ports})`,
                ],
              ]}
              footer={['TOTAL', '', nsLeaf + nsSpine, `${nsCablesNodeLeaf} / ${nsLeaf * nsDownlinks} ports`, '', '']}
            />
            <Table
              headers={['Cable / Optic', 'Count', 'Derivation']}
              rows={[
                [
                  `Node → Leaf (${network.northSouth.fabricType})`,
                  nsCablesNodeLeaf,
                  (() => {
                    const parts: string[] = [];
                    parts.push(`${nodes} GPU × ${server.ethernetPorts || 2}`);
                    if (cpuNodeCount > 0 && cpuNodeProfile) {
                      parts.push(`${cpuNodeCount} CPU × ${cpuNodeProfile.ethernetPorts || 2}`);
                    }
                    if (infraNodeCount > 0 && infraNodeProfile) {
                      parts.push(`${infraNodeCount} Infra × ${infraNodeProfile.ethernetPorts || 2}`);
                    }
                    return parts.join(' + ');
                  })(),
                ],
                [
                  `Leaf → Spine (${network.northSouth.fabricType})`,
                  nsCablesLeafSpine,
                  `${nsLeaf} leaf × ${nsUplinks} uplinks`,
                ],
              ]}
              footer={['TOTAL', nsCablesNodeLeaf + nsCablesLeafSpine, '']}
            />
          </Section>

          <Section title="4 — OOB / Management Network">
            <Table
              headers={['OOB Endpoint Source', 'Count', 'Derivation']}
              rows={[
                ['Host BMC (1/node)', oobHost, `${totalNodes} nodes (GPU+CPU+Infra)`],
                [
                  `DPU OOB (${network.management.oobPortsPerDPU}/DPU × ${network.management.dpusPerNode} DPU/node)`,
                  oobDpu,
                  `${totalNodes} × ${network.management.dpusPerNode} × ${network.management.oobPortsPerDPU}`,
                ],
                [
                  'Fabric switch OOB (IB leaf+spine + NS leaf+spine)',
                  oobSwitches,
                  `${ibLeaf}+${ibSpine}+${nsLeaf}+${nsSpine}`,
                ],
              ]}
              footer={['TOTAL OOB endpoints', oobTotal, '']}
            />
            <Table
              headers={['OOB Switch', 'Ports/Switch', 'Switches Required', 'Derivation']}
              rows={[
                [
                  network.management.switchModel,
                  network.management.portsPerSwitch,
                  oobSwitchCount,
                  `ceil(${oobTotal} ÷ ${network.management.portsPerSwitch})`,
                ],
              ]}
            />
          </Section>

          <Section title="5 — Rack Layout & Power">
            <Table
              headers={['Rack Type', 'Units per Rack', 'Rack Count', 'Note']}
              rows={[
                [
                  'Compute',
                  `${rack.serversPerRack} nodes × ${server.rackUnits} RU = ${computeRuUsed} RU`,
                  computeRacks,
                  `ceil(${nodes} ÷ ${rack.serversPerRack})`,
                ],
                [
                  `IB switches (${network.eastWest.leafSwitch.model})`,
                  '1 RU each',
                  ibLeaf + ibSpine,
                  `${ibLeaf} leaf + ${ibSpine} spine`,
                ],
                [
                  `NS switches (${network.northSouth.leafSwitch.model})`,
                  '2 RU each',
                  nsLeaf + nsSpine,
                  `${nsLeaf} leaf + ${nsSpine} spine`,
                ],
                [`OOB switches (${network.management.switchModel})`, '1 RU each', oobSwitchCount, ''],
                ['Cable management', '1 RU each', cableMgmtRu, '~0.5 RU per network switch'],
                [
                  `Switch racks total (48U)`,
                  `${totalSwitchRu} RU total`,
                  switchRacks,
                  `ceil(${totalSwitchRu} ÷ 48)`,
                ],
              ]}
              footer={['TOTAL RACKS', '', totalRacks, `${computeRacks} compute + ${switchRacks} switch`]}
            />
            <Table
              headers={['Power Scenario', 'Per Rack (kW)', 'Compute Racks', 'Total Compute Power (kW)', 'Note']}
              rows={[
                [
                  `${rack.serversPerRack} nodes/rack`,
                  rackPowerKw.toFixed(1),
                  computeRacks,
                  totalComputePowerKw.toFixed(1),
                  `${rack.serversPerRack} × ${power.powerPerNodeKw} kW/node`,
                ],
              ]}
            />
          </Section>

          <Section title="6 — Full BOM Summary">
            <Table
              headers={['Line', 'Component', 'Qty/Node', 'Total']}
              rows={[
                ['1', gpu.platformName, scaling.gpusPerNode, scaling.totalGPUs],
                ['2', 'Compute nodes', 1, nodes],
                ['3', `IB switch — ${network.eastWest.leafSwitch.model} (leaf)`, '—', ibLeaf],
                ['4', `IB switch — ${network.eastWest.spineSwitch.model} (spine)`, '—', ibSpine],
                [
                  '5',
                  `${network.eastWest.fabricType} cables — node-to-leaf`,
                  network.eastWest.portsPerNode,
                  ibCablesNodeLeaf,
                ],
                ['6', `${network.eastWest.fabricType} cables — leaf-to-spine`, '—', ibCablesLeafSpine],
                ['7', `NS switch — ${network.northSouth.leafSwitch.model} (leaf)`, '—', nsLeaf],
                ['8', `NS switch — ${network.northSouth.spineSwitch.model} (spine)`, '—', nsSpine],
                [
                  '9',
                  `${network.northSouth.fabricType} cables — node-to-leaf`,
                  'Per node',
                  nsCablesNodeLeaf,
                ],
                ['10', `${network.northSouth.fabricType} cables — leaf-to-spine`, '—', nsCablesLeafSpine],
                ['11', 'DPUs', network.management.dpusPerNode, nodes * network.management.dpusPerNode],
                ['12', `OOB switch — ${network.management.switchModel}`, '—', oobSwitchCount],
                ['13', 'Compute racks', `${rack.serversPerRack}/rack`, computeRacks],
                ['14', 'Switch racks', '—', switchRacks],
                ['15', 'Total racks', '—', totalRacks],
                ['16', 'Estimated peak compute power', '—', `${totalComputePowerKw.toFixed(1)} kW`],
              ]}
            />
          </Section>

          <div className="bg-yellow-50 border border-yellow-400 rounded p-3 text-xs text-yellow-900">
            <b>Assumptions:</b> 2-tier (leaf-spine) fat-tree topology. IB and NS downlink ratio applied
            uniformly. Switch RU: IB=1U, NS=2U, OOB=1U. Power figures from vendor datasheets — treat as
            estimates pending actual server vendor power reports. No oversubscription unless downlink ratio
            &lt; 0.5.
          </div>
            </>
          ) : (
            /* Topology Visualization View */
            <TopologyVisualization />
          )}
        </div>
      </div>
    </div>
  );
}

// Main App wrapper with authentication
function App() {
  const { user, loading } = useAuth();

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show landing page if not authenticated
  if (!user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Show main app for authenticated users
  return <MainApp />;
}

export default App;
